import {
  Box,
  CircularProgress,
  Typography,
  useMediaQuery
} from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import { Card } from '@cw-ui/surface-components';
import { styled } from '@mui/material';
import { formatMessage } from 'cw-ui-core';
import Locale from 'src/Locale';
import { injectIntl } from 'react-intl';
import { DataGridPro } from '@mui/x-data-grid-pro';
import Status from '../../Status';
import { setMuiLicense } from '@cw-ui/utils';

const getProductDisplayInfo = product => {
  let productName;
  let productNameForTitle;

  switch (product.name.toLowerCase()) {
    case 'manage':
      productName = 'Classic PSA';
      productNameForTitle = 'Classic PSA™';
      break;
    case 'brightgauge':
      productNameForTitle = 'Brightgauge™';
      break;
    default:
      productName = product.name;
      productNameForTitle = product.name;
      break;
  }

  return {
    productName: productName || product.name,
    productNameForTitle
  };
};

const shouldShowTrademark = product =>
  product.name.toLowerCase() === 'manage' ||
  product.name.toLowerCase() === 'brightgauge';

const SCompatibleApplications = styled(Card)(() => ({
  padding: '16px',
  display: 'flex',
  flexDirection: 'column',
  gap: '15px',
  width: '100%',
  minWidth: 0,
  boxSizing: 'border-box'
}));

const GRID_HEIGHT = {
  laptop: 340,
  desktop: 380,
  large: 420
};

const COLUMN_MIN_WIDTHS = {
  instance: 150,
  status: 100,
  payment: 110,
  instanceType: 140,
  url: 180
};

const AsioSecondStep = props => {
  setMuiLicense();
  const { filteredProducts, isAutomateInAsioUpgrade } = props;
  const isLargeScreen = useMediaQuery('(min-width: 1600px)');
  const isLaptopScreen = useMediaQuery('(min-width: 1200px) and (max-width: 1599px)');

  const gridHeight = isLargeScreen
    ? GRID_HEIGHT.large
    : isLaptopScreen
      ? GRID_HEIGHT.laptop
      : GRID_HEIGHT.desktop;

  const columns = useMemo(() => [
    {
      field: 'instances',
      headerName: 'Instance',
      flex: 1,
      minWidth: COLUMN_MIN_WIDTHS.instance,
      sortable: false,
      renderCell: ({ row }) => {
        const { productName, productNameForTitle } = getProductDisplayInfo(row);

        return (
          <Box sx={{ minWidth: 0, width: '100%', py: 0.5 }}>
            <Typography
              title={productNameForTitle}
              component="p"
              sx={{
                fontSize: '16px',
                fontWeight: 'bold',
                textTransform: 'uppercase',
                fontFamily: 'CeraPro, sans-serif',
                color: '#22326E',
                m: 0,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
            >
              {productName}
              {shouldShowTrademark(row) && (
                <Box
                  component="sup"
                  sx={{
                    fontSize: '10px',
                    fontWeight: 'bold',
                    color: '#22326E',
                    textTransform: 'none'
                  }}
                >
                  {formatMessage(Locale.branding_trademark)}
                </Box>
              )}
            </Typography>
            <Typography
              title={row.instanceIdentifier}
              component="p"
              sx={{
                fontSize: '12px',
                color: '#7d7d7d',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                width: '100%',
                maxWidth: '100%',
                overflow: 'hidden',
                fontFamily: 'sans-serif',
                m: 0
              }}
            >
              {row.instanceIdentifier}
            </Typography>
          </Box>
        );
      }
    },
    {
      field: 'status',
      headerName: 'Status',
      flex: 1,
      minWidth: COLUMN_MIN_WIDTHS.status,
      sortable: false,
      renderCell: ({ row }) => {
        return (
          <Status type={row.hasActiveInstance ? 'active' : 'inactive'}>
            {row.hasActiveInstance ? 'Active' : 'Inactive'}
          </Status>
        );
      }
    },
    {
      field: 'payment',
      headerName: 'Payment',
      flex: 1,
      minWidth: COLUMN_MIN_WIDTHS.payment,
      sortable: false,
      renderCell: ({ row }) => {
        return (
          <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', minWidth: 0 }}>
            <Status type={row.purchased ? 'active' : 'inactive'}>
              {row.purchased ? 'Purchased' : 'Not Purchased'}
            </Status>
          </Box>
        );
      }
    },
    !isAutomateInAsioUpgrade ? {
      field: 'instance_type',
      headerName: 'Instance Type',
      sortable: false,
      flex: 1,
      minWidth: COLUMN_MIN_WIDTHS.instanceType,
      renderCell: ({ row }) => {
        return (
          <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', minWidth: 0, overflow: 'hidden' }}>
            <Typography
              component="span"
              title={row.installType}
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                fontSize: '12px',
                width: '100%'
              }}
            >
              {row.installType}
            </Typography>
          </Box>
        );
      }
    } : null,
    isAutomateInAsioUpgrade ? {
      field: 'url',
      headerName: 'URL',
      sortable: false,
      flex: 1,
      minWidth: COLUMN_MIN_WIDTHS.url,
      renderCell: ({ row }) => {
        return (
          <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', minWidth: 0, width: '100%' }}>
            <Typography
              component="span"
              title={row.launchUrl}
              sx={{
                overflow: 'hidden',
                whiteSpace: 'nowrap',
                textOverflow: 'ellipsis',
                fontSize: '12px',
                width: '100%'
              }}
            >
              {row.launchUrl}
            </Typography>
          </Box>
        );
      }
    } : null
  ].filter(Boolean), [isAutomateInAsioUpgrade]);

  const CompatibleApplications = ({ filteredProducts }) => {
    const [products, setProducts] = useState([]);

    const [isLoading, setLoading] = useState(false);

    useEffect(() => {
      if (products.length <= 0) {
        setLoading(true);
      }

      let data = filteredProducts;
      if (data && products.length <= 0) {
        setLoading(false);
        data = mapProducts(data);
        setProducts(data);
      }
    }, [filteredProducts, products.length]);

    const mapProducts = filteredProducts => {
      return filteredProducts.map((product, index) => ({
        ...product,
        id: index
      }));
    };

    return (
      <SCompatibleApplications>
        <Box sx={{ width: '100%', minWidth: 0 }}>
          <DataGridPro
            sx={{
              width: '100%',
              height: `${gridHeight}px`,
              border: 'none',
              '& .MuiDataGrid-virtualScroller': {
                overflowX: 'auto'
              },
              '& .MuiDataGrid-columnHeaders': {
                backgroundColor: '#fff',
                color: '#101120'
              },
              '& .MuiDataGrid-cell': {
                overflow: 'hidden'
              },
              '& .MuiDataGrid-columnHeaderTitle': {
                whiteSpace: 'nowrap',
                overflow: 'visible',
                textOverflow: 'clip'
              },
              '& .MuiDataGrid-scrollbar--horizontal': {
                display: 'block'
              }
            }}
            rows={products}
            checkboxSelection={false}
            columns={columns}
            slots={{ toolbar: null }}
            slotProps={{ toolbar: null }}
            getRowHeight={() => 'auto'}
            hideFooter
            getRowId={row => row.id}
            loading={isLoading}
            disableColumnMenu
            disableColumnResize
            components={{
              LoadingOverlay: () => (
                <Box
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  height={`${gridHeight}px`}
                >
                  <CircularProgress />
                </Box>
              )
            }}
          />
        </Box>
      </SCompatibleApplications>
    );
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Typography
        fontWeight="300"
        pl="16px"
        fontSize="26px"
        sx={{ fontFamily: 'sans-serif' }}
      >
        {formatMessage(Locale.asio_step1_title)}
      </Typography>
      <Typography
        variant="body2"
        fontSize="13px"
        pl="16px"
        mb="16px"
        fontWeight="400"
        color="#767786"
        lineHeight="18px"
        sx={{ fontFamily: 'sans-serif' }}
      >
        {isAutomateInAsioUpgrade ? formatMessage(Locale.asio_step2_sub_alternate) : formatMessage(Locale.asio_step2_sub)}
      </Typography>
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: { xs: 'center', md: 'stretch' },
          width: '100%',
          fontFamily: 'sans-serif'
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            p: '16px',
            width: { xs: '100%', md: '60%' },
            minWidth: 0,
            boxSizing: 'border-box'
          }}
        >
          <CompatibleApplications filteredProducts={filteredProducts} />
        </Box>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            p: '16px',
            width: { xs: '100%', md: '40%' },
            minWidth: 0,
            boxSizing: 'border-box'
          }}
        >
          <Box
            component="img"
            src="/images/AsioPage/Dialog-illustration2.png"
            alt=""
            sx={{
              width: '100%',
              maxHeight: {
                xs: 280,
                md: isLargeScreen ? 420 : isLaptopScreen ? 340 : 380
              },
              objectFit: 'contain'
            }}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default injectIntl(AsioSecondStep);
